import React, { useEffect, useRef } from 'react';
import 'bootstrap-icons/font/bootstrap-icons.css';

const services = [
  { icon: 'bi-phone',           title: 'Mobile App Development',        description: 'Tailored mobile app development services that design and develop your dream app as your vision.' },
  { icon: 'bi-receipt',         title: 'Billing Solutions',              description: 'Comprehensive billing solutions and payment software built for speed, accuracy and scale.' },
  { icon: 'bi-globe',           title: 'Website Development',            description: 'We design mobile-responsive websites with powerful visual tools and full creative control.' },
  { icon: 'bi-window-stack',    title: 'Web Application Development',    description: 'Robust, scalable web applications that evolve with your business needs and user expectations.' },
  { icon: 'bi-headset',         title: 'Software Support & Development', description: 'Rapid, repeatable software support keeping your applications running at peak performance.' },
  { icon: 'bi-shield-lock',     title: 'Billing Systems & Payments',     description: 'Secure billing and payment integration ensuring the highest level of data protection.' },
  { icon: 'bi-credit-card',     title: 'Billing Payment Integration',    description: 'Seamless billing integration solutions for a smooth, efficient transaction experience.' },
  { icon: 'bi-tools',           title: 'IT Support Projects',            description: 'Comprehensive IT support services ensuring seamless operations and timely resolutions.' },
];

const ServicesOffered = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('so-in-view');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    const cards = sectionRef.current?.querySelectorAll('[data-so-card]') || [];
    cards.forEach(c => observer.observe(c));
    return () => cards.forEach(c => observer.unobserve(c));
  }, []);

  return (
    <section className="so-section" ref={sectionRef}>
      <div className="so-inner">
        <div className="so-header">
          <h2 className="so-title">
            Specific Services Offered for Your{' '}
            <span className="so-accent">Business Needs</span>
          </h2>
          <p className="so-sub">
            With 10+ discrete services across technology strategy, migration, engineering,
            modern design, and managed services — Bellary Info Tech Solutions has limitless potential.
          </p>
        </div>

        <div className="so-grid">
          {services.map((svc, i) => (
            <div
              key={i}
              className="so-card"
              data-so-card
              style={{ animationDelay: `${i * 0.07}s` }}
            >
              <div className="so-card-icon">
                <i className={`bi ${svc.icon}`}></i>
              </div>
              <h3 className="so-card-title">{svc.title}</h3>
              <p className="so-card-desc">{svc.description}</p>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .so-section {
          background: var(--slate-950, #020617);
          padding: 6rem 2rem;
          position: relative;
          overflow: hidden;
        }
        .so-section::before {
          content: '';
          position: absolute;
          top: -10%; left: -5%;
          width: 450px; height: 450px;
          background: radial-gradient(circle, rgba(29,78,216,0.15), transparent 70%);
          border-radius: 50%;
          pointer-events: none;
        }
        .so-inner {
          max-width: 1200px;
          margin: 0 auto;
          position: relative;
          z-index: 2;
        }
        .so-header {
          text-align: center;
          max-width: 700px;
          margin: 0 auto 3.5rem;
        }
        .so-title {
          font-size: clamp(1.8rem, 3vw, 2.5rem);
          font-weight: 800;
          color: white;
          margin-bottom: 1rem;
          letter-spacing: -0.04em;
          line-height: 1.2;
        }
        .so-accent {
          background: var(--grad-text, linear-gradient(135deg,#60A5FA,#22D3EE));
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .so-sub {
          font-size: 1rem;
          color: rgba(255,255,255,0.55);
          line-height: 1.8;
          margin: 0;
        }
        .so-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1.5rem;
        }
        .so-card {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 18px;
          padding: 2rem 1.5rem;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          opacity: 0;
          transform: translateY(30px);
          transition: transform 0.35s ease, box-shadow 0.35s ease,
                      border-color 0.35s ease, background 0.3s ease;
          cursor: default;
        }
        .so-card.so-in-view {
          animation: soReveal 0.55s ease-out forwards;
        }
        @keyframes soReveal {
          from { opacity: 0; transform: translateY(28px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .so-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 50px rgba(0,0,0,0.35);
          border-color: rgba(59,130,246,0.3);
          background: rgba(59,130,246,0.07);
        }
        .so-card-icon {
          width: 50px; height: 50px;
          background: rgba(59,130,246,0.15);
          border: 1px solid rgba(59,130,246,0.25);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.3s ease, background 0.3s ease;
        }
        .so-card:hover .so-card-icon {
          transform: scale(1.1) rotate(5deg);
          background: rgba(59,130,246,0.25);
        }
        .so-card-icon i {
          font-size: 1.4rem;
          color: var(--blue-400, #60A5FA);
        }
        .so-card-title {
          font-size: 1rem;
          font-weight: 700;
          color: white;
          margin: 0;
          line-height: 1.3;
          transition: color 0.3s ease;
        }
        .so-card:hover .so-card-title {
          color: var(--blue-300, #93C5FD);
        }
        .so-card-desc {
          font-size: 0.85rem;
          color: rgba(255,255,255,0.5);
          line-height: 1.7;
          margin: 0;
        }
        @media (max-width: 1024px) {
          .so-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 768px) {
          .so-section { padding: 4rem 1.25rem; }
          .so-grid { gap: 1.25rem; }
        }
        @media (max-width: 480px) {
          .so-section { padding: 3rem 1rem; }
          .so-grid { grid-template-columns: 1fr; }
          .so-card { padding: 1.5rem 1.25rem; }
        }
      `}</style>
    </section>
  );
};

export default ServicesOffered;
